import { useCallback, useEffect, useRef, useState } from 'react';
import type { RichMenuLayout } from '@foundation/shared';
import { drawMenu } from '../lib/richMenuImage';
import { api } from '../lib/api';
import { useBranding } from '../lib/branding';
import { ErrorAlert, Loading } from '../components/ui';

interface RichMenuState {
  configured: Record<string, string | undefined>;
  ready: { staff: boolean; public: boolean };
  liff: { staffUrl: string; donorUrl: string };
  layouts: RichMenuLayout[];
}

export default function RichMenus() {
  const branding = useBranding();
  const [state, setState] = useState<RichMenuState | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [logo, setLogo] = useState<HTMLImageElement | null>(null);
  const canvases = useRef<Record<string, HTMLCanvasElement | null>>({});

  const load = useCallback(async () => {
    setError(null);
    try {
      setState(await api<RichMenuState>('/settings/line/rich-menus'));
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!branding.logoDataUri) return setLogo(null);
    const img = new Image();
    img.onload = () => setLogo(img);
    img.src = branding.logoDataUri;
  }, [branding.logoDataUri]);

  useEffect(() => {
    if (!state) return;
    for (const layout of state.layouts) {
      const canvas = canvases.current[layout.key];
      if (canvas) drawMenu(canvas, layout, branding.nameTh, logo);
    }
  }, [state, branding.nameTh, logo]);

  async function create(layout: RichMenuLayout) {
    const canvas = canvases.current[layout.key];
    if (!canvas) return;
    setBusy(layout.key);
    setError(null);
    setFlash(null);
    try {
      const res = await api<{ richMenuId: string }>('/settings/line/rich-menus', {
        method: 'POST',
        body: { key: layout.key, imageDataUri: canvas.toDataURL('image/png') },
      });
      setFlash(`สร้างเมนู "${layout.name}" แล้ว (${res.richMenuId})`);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  const liffMissing = (oa: 'staff' | 'public') =>
    !state ||
    (oa === 'staff' ? !state.liff.staffUrl : !state.liff.donorUrl);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>เมนูลัดบน LINE</h1>
          <p>ปุ่มด้านล่างหน้าแชท ที่พาเจ้าหน้าที่และผู้บริจาคเข้าหน้า LIFF</p>
        </div>
      </div>

      <ErrorAlert error={error} />
      {flash && <div className="alert success">{flash}</div>}

      {!state ? (
        <Loading />
      ) : (
        <>
          <div className="alert info">
            รูปเมนูถูกวาดจากเบราว์เซอร์ของคุณตรงนี้เลย — ที่เห็นด้านล่างคือของจริงที่จะขึ้นในแอป
            LINE พื้นที่กดของแต่ละปุ่มคำนวณจากค่าชุดเดียวกับรูป จึงตรงกันเสมอ
            <br />
            ใส่โลโก้ที่หน้าตั้งค่าระบบก่อน แล้วโลโก้จะขึ้นบนเมนูนี้ด้วย
          </div>

          {state.layouts.map((layout) => {
            const existing = state.configured[layout.key];
            const oaReady = layout.oa === 'staff' ? state.ready.staff : state.ready.public;
            const missing = liffMissing(layout.oa);
            return (
              <div className="card" key={layout.key}>
                <h2>
                  {layout.name}{' '}
                  <span className="badge">{layout.oa === 'staff' ? 'OA เจ้าหน้าที่' : 'OA สาธารณะ'}</span>{' '}
                  {existing ? (
                    <span className="badge green">สร้างแล้ว</span>
                  ) : (
                    <span className="badge amber">ยังไม่ได้สร้าง</span>
                  )}
                </h2>
                <p className="hint">{layout.description}</p>

                <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                  <canvas
                    ref={(el) => {
                      canvases.current[layout.key] = el;
                    }}
                    style={{
                      width: '100%',
                      maxWidth: 620,
                      height: 'auto',
                      border: '1px solid var(--c-border)',
                      borderRadius: 8,
                      display: 'block',
                    }}
                  />
                </div>

                {!oaReady && (
                  <div className="alert warn">
                    ยังไม่ได้ตั้ง access token ของ OA นี้ — สร้างเมนูไม่ได้
                  </div>
                )}
                {missing && (
                  <div className="alert warn">
                    ยังไม่ได้ตั้ง LIFF ID ของ{' '}
                    {layout.oa === 'staff' ? 'เจ้าหน้าที่' : 'ผู้บริจาค'} ใน{' '}
                    <span className="mono">wrangler.toml</span> — ปุ่มจะเปิดหน้าไม่ได้
                  </div>
                )}

                <div className="btn-row">
                  <button
                    className="btn"
                    disabled={busy !== null || !oaReady || missing}
                    onClick={() => void create(layout)}
                  >
                    {busy === layout.key
                      ? 'กำลังสร้าง…'
                      : existing
                        ? 'สร้างใหม่แทนของเดิม'
                        : 'สร้างเมนูนี้'}
                  </button>
                  {existing && <span className="mono hint">{existing}</span>}
                </div>

                <details style={{ marginTop: 10 }}>
                  <summary className="hint">ปุ่มในเมนูนี้พาไปไหนบ้าง</summary>
                  <ul className="hint" style={{ paddingLeft: 20 }}>
                    {layout.buttons.map((b) => (
                      <li key={b.label}>
                        {b.icon} {b.label} →{' '}
                        {b.path ? (
                          <span className="mono">{b.path}</span>
                        ) : (
                          <>ส่งข้อความ “{b.text ?? b.label}” เข้าแชท</>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            );
          })}

          <div className="alert warn">
            <strong>สร้างใหม่แทนของเดิมแล้วเมนูเก่าจะถูกลบ</strong> —
            เจ้าหน้าที่ที่ผูกเมนูเก่าไว้จะเห็นเมนูใหม่ในรอบถัดไปที่เปิดแชท
            ถ้าเมนูของเจ้าหน้าที่ไม่เปลี่ยน ให้เข้าหน้าจัดการเจ้าหน้าที่แล้วปลด–ผูกบัญชี LINE ใหม่
          </div>
        </>
      )}
    </>
  );
}
